// btw-front/src/components/admin/dashboard/DealsTab.jsx
// [딜 관리] 탭 — 전체 딜 목록·필터·정렬·KPI
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatCard } from "../../ui/StatCard";
import { Badge } from "../../ui/Badge";
import { Input } from "../../ui/Input";
import { EmptyState } from "../../ui/EmptyState";
import * as adminDeals from "../../../api/adminDeals";
import { SALES_STAGES, IN_PROGRESS_STAGES } from "../../../constants/dealStatus";
import { formatQuarter } from "../../../lib/format";
import { usePartnerSession } from "../../../context/PartnerSessionContext";
import { useQueryState, parseSort, formatSort } from "../../../lib/useQueryState";
import { formatKRW, dealClickPath, SortTh, Tabs } from "./shared";

export default function DealsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = usePartnerSession();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // 필터·검색어·정렬은 URL 에 보관 — 상세에서 돌아와도 같은 목록·스크롤 위치로 복원
  const [filter, setFilter] = useQueryState("status", "all");
  const [q, setQ] = useQueryState("q");
  // 정렬 상태 — null 이면 서버 기본 순서(최신 등록순) 유지. URL 에는 "key.dir" 로 둔다
  const [sortRaw, setSortRaw] = useQueryState("sort");
  const sort = useMemo(() => parseSort(sortRaw), [sortRaw]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminDeals
      .list({ status: filter === "all" ? undefined : filter, q: q || undefined })
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter, q]);

  const kpi = useMemo(
    () => ({
      total: items.length,
      inProgress: items.filter((d) => IN_PROGRESS_STAGES.includes(d.status)).length,
      adoption: items.filter((d) => d.status === "adoption").length,
      lost: items.filter((d) => d.status === "lost").length,
    }),
    [items],
  );

  // 컬럼 헤더 클릭 정렬 — 같은 컬럼 재클릭 시 방향 전환, 다른 컬럼은 오름차순으로 시작
  const toggleSort = (key) =>
    setSortRaw((raw) => {
      const s = parseSort(raw);
      return formatSort(
        s && s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
      );
    });
  const sortedItems = useMemo(() => {
    if (!sort) return items;
    const mul = sort.dir === "asc" ? 1 : -1;
    // 정렬 값 — partner/customer 는 회사명, poDate 는 'yyyyQn'(사전순=시간순), createdAt 은 ISO 문자열
    const val = (d) => {
      if (sort.key === "partner") return d.displayCompanyName || d.partnerCompanyName || "";
      if (sort.key === "customer") return d.customerCompany || "";
      if (sort.key === "poDate") return d.poExpectedQuarter || "";
      return d.createdAt || "";
    };
    return [...items].sort((a, b) => val(a).localeCompare(val(b), "ko") * mul);
  }, [items, sort]);

  return (
    <div>
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label={t("partners.dashboard.kpi.total")} value={kpi.total} />
        <StatCard label={t("partners.dashboard.kpi.inProgress")} value={kpi.inProgress} />
        <StatCard label={t("partners.dashboard.kpi.adoption")} value={kpi.adoption} />
        <StatCard label={t("partners.dashboard.kpi.lost")} value={kpi.lost} />
      </section>

      {/* 필터 바 — 상단 상태 Tabs, 하단 키워드 검색 + 딜 등록 */}
      <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-3">
        <Tabs
          value={filter}
          onChange={setFilter}
          items={[
            { key: "all", label: t("admin.filters.allStatus") },
            ...SALES_STAGES.map((s) => ({ key: s, label: t(`partners.deals.status.${s}`) })),
          ]}
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Input
            name="search"
            placeholder={t("admin.filters.searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-60"
          />
          <button
            type="button"
            onClick={() => navigate("/admin/deals/new")}
            className="btw-btn-accent ml-auto"
          >
            {t("admin.deals.addDeal")}
          </button>
        </div>
      </section>

      <section className="mt-3">
        {loading ? (
          <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
        ) : items.length === 0 ? (
          <EmptyState title={t("admin.empty")} />
        ) : (
          <>
            {/* 데스크탑(md+) 테이블 — 긴 텍스트 컬럼(파트너·프로젝트·고객사)은 줄바꿈 허용해
                가로 스크롤 없이 전체 표시. 금액·분기·상태·날짜는 nowrap 유지. */}
            <div className="hidden rounded-2xl border border-slate-200 bg-white md:block">
              {/* auto 레이아웃 — 금액·일자·상태·등록일은 w-px+nowrap 으로 내용 최소폭만 차지하고,
                  텍스트 3컬럼(파트너·프로젝트·고객사)은 아래 % 폭으로 가로 공간을 나눠 가짐.
                  ↓ 이 3개 % 값만 조절하면 텍스트 컬럼 너비 배분이 바뀜(합 100% 권장). */}
              <table className="w-full text-sm">
                <colgroup>
                  <col className="w-[28%]" /> {/* 파트너사 */}
                  <col className="w-[42%]" /> {/* 프로젝트명 */}
                  <col className="w-[30%]" /> {/* 고객사 */}
                  <col className="w-px" /> {/* 예상 금액 — 내용 최소폭 */}
                  <col className="w-px" /> {/* 예상 일자 — 내용 최소폭 */}
                  <col className="w-px" /> {/* 상태 — 내용 최소폭 */}
                  <col className="w-px" /> {/* 등록일 — 내용 최소폭 */}
                </colgroup>
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <SortTh
                      sort={sort}
                      onToggle={toggleSort}
                      sortKey="partner"
                      label={t("admin.table.partner")}
                    />
                    <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                      {t("admin.table.projectName")}
                    </th>
                    <SortTh
                      sort={sort}
                      onToggle={toggleSort}
                      sortKey="customer"
                      label={t("admin.table.customer")}
                    />
                    <th className="whitespace-nowrap px-4 py-2.5 text-right font-medium">
                      {t("admin.table.poAmount")}
                    </th>
                    <SortTh
                      sort={sort}
                      onToggle={toggleSort}
                      sortKey="poDate"
                      label={t("admin.table.poDate")}
                    />
                    <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                      {t("admin.table.status")}
                    </th>
                    <SortTh
                      sort={sort}
                      onToggle={toggleSort}
                      sortKey="createdAt"
                      label={t("admin.table.createdAt")}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedItems.map((d) => (
                    <tr
                      key={d.id}
                      onClick={() => navigate(dealClickPath(d, profile.userId))}
                      className="cursor-pointer transition hover:bg-slate-50"
                    >
                      <td className="break-words px-4 py-2.5 align-top text-slate-700">
                        <div className="flex items-start gap-2">
                          <span className="min-w-0 break-words">
                            {d.displayCompanyName || d.partnerCompanyName || "-"}
                          </span>
                          {d.isAdminCreated ? (
                            <span className="mt-0.5 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                              {t("admin.table.adminBadge")}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="break-words px-4 py-2.5 align-top font-medium text-slate-900">
                        {d.projectName}
                      </td>
                      <td className="break-words px-4 py-2.5 align-top text-slate-700">
                        {d.customerCompany}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right align-top tabular-nums text-slate-700">
                        {formatKRW(d.poAmount)} {t("common.unitKrw")}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 align-top text-slate-600">
                        {formatQuarter(d.poExpectedQuarter) || "-"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 align-top">
                        <Badge tone={d.status}>{t(`partners.deals.status.${d.status}`)}</Badge>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 align-top text-slate-500">
                        {d.createdAt && d.createdAt.slice(0, 10)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 모바일(md 미만) 카드 — 좁은 화면에선 표 대신 카드로 가독성 확보 */}
            <div className="grid gap-2 md:hidden">
              {sortedItems.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => navigate(dealClickPath(d, profile.userId))}
                  className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-accent/40"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                      {d.projectName}
                    </span>
                    <Badge tone={d.status}>{t(`partners.deals.status.${d.status}`)}</Badge>
                  </div>
                  <div className="truncate text-xs text-slate-600">{d.customerCompany}</div>
                  <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate">
                        {d.displayCompanyName || d.partnerCompanyName || "-"}
                      </span>
                      {d.isAdminCreated ? (
                        <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                          {t("admin.table.adminBadge")}
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {formatKRW(d.poAmount)} {t("common.unitKrw")}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
